import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Save } from 'lucide-react';

interface MapSettings {
  defaultCenter: [number, number];
  defaultZoom: number;
  mapProvider: 'osm' | 'satellite' | 'terrain';
  showLegend: boolean;
  showScale: boolean;
  showCompass: boolean;
  trackingInterval: number; // in seconds
  heatmapRadius: number;
  maxZoom: number;
  minZoom: number;
}

export function MapSettings() {
  const [settings, setSettings] = useState<MapSettings>({
    defaultCenter: [-33.865143, 151.209900],
    defaultZoom: 13,
    mapProvider: 'osm',
    showLegend: true,
    showScale: true,
    showCompass: true,
    trackingInterval: 30,
    heatmapRadius: 20,
    maxZoom: 18,
    minZoom: 8
  });

  const handleSave = () => {
    // Save settings to backend
    console.log('Saving settings:', settings);
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Map Display Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Default Latitude</Label>
              <Input 
                type="number" 
                value={settings.defaultCenter[0]}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  defaultCenter: [parseFloat(e.target.value), prev.defaultCenter[1]]
                }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Default Longitude</Label>
              <Input 
                type="number" 
                value={settings.defaultCenter[1]}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  defaultCenter: [prev.defaultCenter[0], parseFloat(e.target.value)]
                }))}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Map Provider</Label>
            <Select 
              value={settings.mapProvider}
              onValueChange={(value: 'osm' | 'satellite' | 'terrain') => 
                setSettings(prev => ({ ...prev, mapProvider: value }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select map provider" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="osm">OpenStreetMap</SelectItem>
                <SelectItem value="satellite">Satellite</SelectItem>
                <SelectItem value="terrain">Terrain</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Show Legend</Label>
              <Switch 
                checked={settings.showLegend}
                onCheckedChange={(checked) => 
                  setSettings(prev => ({ ...prev, showLegend: checked }))
                }
              />
            </div>

            <div className="flex items-center justify-between">
              <Label>Show Scale</Label>
              <Switch 
                checked={settings.showScale}
                onCheckedChange={(checked) => 
                  setSettings(prev => ({ ...prev, showScale: checked }))
                }
              />
            </div>

            <div className="flex items-center justify-between">
              <Label>Show Compass</Label>
              <Switch 
                checked={settings.showCompass}
                onCheckedChange={(checked) => 
                  setSettings(prev => ({ ...prev, showCompass: checked }))
                }
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Tracking Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label>Tracking Interval (seconds)</Label>
            <Input 
              type="number" 
              value={settings.trackingInterval}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                trackingInterval: parseInt(e.target.value)
              }))}
            />
          </div>

          <div className="space-y-2">
            <Label>Heatmap Radius</Label>
            <Input 
              type="number" 
              value={settings.heatmapRadius}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                heatmapRadius: parseInt(e.target.value)
              }))}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Maximum Zoom Level</Label>
              <Input 
                type="number" 
                value={settings.maxZoom}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  maxZoom: parseInt(e.target.value)
                }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Minimum Zoom Level</Label>
              <Input 
                type="number" 
                value={settings.minZoom}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  minZoom: parseInt(e.target.value)
                }))}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} className="flex items-center gap-2">
          <Save className="w-4 h-4" />
          Save Settings
        </Button>
      </div>
    </div>
  );
}